import React, { useEffect, useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { QRCodeCanvas } from 'qrcode.react'
import html2pdf from 'html2pdf.js'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001'

const CheckInPassPage = () => {
	const { id } = useParams()
	const [checkIn, setCheckIn] = useState(undefined)
	const passRef = useRef()

	useEffect(() => {
		fetch(`${BASE_URL}/api/checkIns/${id}`)
			.then((res) => res.json())
			.then((data) => setCheckIn(data))
			.catch(() => setCheckIn(null))
	}, [id])

	const handleDownloadPdf = async () => {
		if (!passRef.current) return

		await document.fonts.ready

		const noPrintElements = passRef.current.querySelectorAll('.no-print')
		noPrintElements.forEach((el) => (el.style.display = 'none'))

		await html2pdf()
			.from(passRef.current)
			.set({
				margin: 0,
				filename: `${checkIn.travelerName || 'Pass'}.pdf`,
				html2canvas: {
					scale: 2,
					useCORS: true,
					backgroundColor: '#ffc6b8',
					scrollY: 0,
				},
				jsPDF: {
					unit: 'in',
					format: 'letter',
					orientation: 'portrait',
				},
				pagebreak: { mode: ['avoid-all'] },
			})
			.save()

		noPrintElements.forEach((el) => (el.style.display = ''))
	}

	if (checkIn === undefined) {
		return <p style={{ padding: '2rem' }}>Loading pass...</p>
	}

	if (checkIn === null) {
		return (
			<div style={styles.page}>
				<div style={styles.container}>
					<h1 style={styles.heading}>Pass Not Found</h1>
					<p style={styles.subtext}>
						We couldn't find a check-in with that ID.
					</p>
					<div style={styles.section}>
						<p style={styles.data}>
							Please check the link or contact experience support if you believe
							this is an error.
						</p>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div ref={passRef} style={styles.page}>
			{/* Hide .no-print in PDF */}
			<style>{`
				@media print {
					.no-print {
						display: none !important;
					}
				}
			`}</style>

			<link
				href='https://fonts.googleapis.com/css2?family=Anton&family=Fraunces:wght@500&family=Inter&display=swap'
				rel='stylesheet'
			/>

			<div style={styles.container}>
				<h1 style={styles.heading}>{checkIn.experienceName}</h1>
				{checkIn.orgName && (
					<p style={styles.subtext}>Presented by {checkIn.orgName}</p>
				)}

				<div style={styles.section}>
					<p style={styles.label}>
						<strong>Name:</strong>
					</p>
					<p style={styles.data}>{checkIn.travelerName}</p>

					<p style={styles.label}>
						<strong>Email:</strong>
					</p>
					<p style={styles.data}>{checkIn.travelerEmail}</p>

					<p style={styles.label}>
						<strong>Your Check-in Location:</strong>
					</p>
					<p style={styles.data}>{checkIn.stationName}</p>

					<p style={styles.label}>
						<strong>Your Check-in Date:</strong>
					</p>
					<p style={styles.data}>
						{checkIn.scheduledCheckInDate
							? checkIn.scheduledCheckInDate.slice(0, 10)
							: ''}
					</p>
				</div>

				{checkIn.purchasedItems?.filter((item) => item.type === 'addition')
					.length > 0 && (
					<div style={styles.section}>
						<h2 style={styles.subheading}>Purchased Items</h2>
						<ul style={styles.list}>
							{checkIn.purchasedItems
								.filter((item) => item.type === 'addition')
								.map((item, idx) => (
									<li key={idx} style={styles.listItem}>
										{item.name}
									</li>
								))}
						</ul>
					</div>
				)}

				<div style={styles.section}>
					<h2 style={styles.subheading}>Your QR Code</h2>
					<div style={styles.qrWrapper}>
						<div style={styles.qrContainer}>
							<div style={styles.qrBackground}>
								<QRCodeCanvas
									value={checkIn.id.toString()}
									size={160}
									level='H'
									style={styles.qrCode}
								/>
							</div>
							<p style={styles.qrId}>ID: {checkIn.id}</p>
						</div>
					</div>
				</div>

				<div className='no-print' style={styles.section}>
					<button style={styles.button} onClick={handleDownloadPdf}>
						Download PDF Pass
					</button>
				</div>
			</div>
		</div>
	)
}

const styles = {
	page: {
		backgroundColor: '#ffc6b8',
		minHeight: '100vh',
		padding: '2rem 1rem',
		boxSizing: 'border-box',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	container: {
		backgroundColor: '#fff',
		width: '100%',
		maxWidth: '500px',
		padding: '2rem',
		borderRadius: '12px',
		boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
		fontFamily: 'Inter, sans-serif',
		color: '#f5493e',
	},
	heading: {
		fontFamily: 'Anton, sans-serif',
		fontSize: '2rem',
		textAlign: 'center',
		marginBottom: '0.5rem',
	},
	subtext: {
		textAlign: 'center',
		fontFamily: 'Fraunces, serif',
		fontSize: '0.9rem',
		fontStyle: 'italic',
		marginBottom: '2rem',
	},
	subheading: {
		fontFamily: 'Fraunces, serif',
		fontSize: '1.5rem',
		marginBottom: '1rem',
	},
	section: {
		marginBottom: '2rem',
	},
	label: {
		marginBottom: '0.25rem',
		fontWeight: 'bold',
	},
	data: {
		marginBottom: '1rem',
	},
	list: {
		paddingLeft: '1.2rem',
	},
	listItem: {
		marginBottom: '0.5rem',
	},
	qrWrapper: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		padding: '1.5rem',
		background: '#fff4f1',
		borderRadius: '12px',
		border: '5px solid #f5493e',
		boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
	},
	qrContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
	qrBackground: {
		backgroundColor: '#ffffff',
		padding: '12px',
		borderRadius: '16px',
	},
	qrId: {
		marginTop: '0.5rem',
		fontSize: '0.75rem',
		color: '#c53a2c',
		textAlign: 'center',
		fontFamily: 'Inter, sans-serif',
	},
	button: {
		width: '100%',
		padding: '0.75rem',
		fontSize: '1rem',
		backgroundColor: '#f5493e',
		color: '#fff',
		border: 'none',
		borderRadius: '8px',
		fontWeight: 'bold',
		cursor: 'pointer',
		transition: 'background 0.3s ease',
	},
}

export default CheckInPassPage
